import "./index.scss";

import {
  Box,
  CircularProgress,
  SelectChangeEvent,
  Table,
  TableBody,
  TableContainer,
} from "@mui/material";
import Pagination, { PAGE_OPTIONS } from "components/Pagination";
import StaffsAddEditComp from "components/StaffsAddEdit";
import { useQueryParams } from "hooks/useQueryParams";
import { TStaff } from "models/staff";
import { TPagination } from "modules/api/type";
import { deleteStaff, getStaffList, updateStaffStatus } from "modules/staffs/staffServices";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import ConfirmationBox from "../../components/ConfirmationBox";
import Filter from "./Filter";
import { TableHeader, TableItem } from "./TableComponents";

function StaffAccounts() {
  const [data, setData] = useState<TStaff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams, defaultParams, setObjParams] = useQueryParams({
    limit: 10,
    page: 1,
    search: "",
    search_by: "LAST_NAME",
    sort_by: "LAST_NAME",
    sort_order: "ASC",
    status: "",
  });
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });
  const [showStaffAddEditModal, setShowStaffAddEditModal] = useState<boolean>(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showStaffDeleteModal, setShowStaffDeleteModal] = useState<boolean>(false);
  const [currentUserToBeDelete, setCurrentUserToBeDelete] = useState(null);

  const fetchData = useCallback(
    async (parameters) => {
      if (loading) return;
      setLoading(true);
      let result = await getStaffList(parameters);
      setLoading(false);
      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
      }
    },
    [setData, setPagination, loading, setLoading]
  );

  useEffect(() => {
    fetchData(params);
  }, [params]);

  const onChangeFilterHandler = (obj) => {
    setObjParams(obj);
    let temp: any = {
      ...params,
      ...obj,
      page: 1,
    };
    fetchData(temp);
  };

  const handleSortChange = (sort_by: string, sort_order: string) => {
    let temp = { ...params, page: 1, sort_by, sort_order };
    setObjParams({
      page: 1,
      sort_by,
      sort_order,
    });
    fetchData(temp);
  };

  const handlePaginationOptionChange = (event: SelectChangeEvent<any>) => {
    let temp = { ...params, limit: event.target.value, page: 1 };
    setParams("limit", event.target.value);
    fetchData(temp);
  };

  const handlePageChange = (page: number) => {
    let temp = { ...params, page };
    setParams("page", page);
    fetchData(temp);
  };

  const onUpdateStatus = async (id, value) => {
    try {
      let result = await updateStaffStatus(id, { status: value });
      if (result.success) {
        toast("Staff status updated successfully", { type: "success" });
        return true;
      } else {
        toast("Staff status update failed", { type: "error" });
        return false;
      }
    } catch (e) {
      toast("Staff status update failed", { type: "error" });
      return false;
    }
  };

  const onDeleteConfirm = (id) => {
    setCurrentUserToBeDelete(id);
    setShowStaffDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    try {
      const result = await deleteStaff(currentUserToBeDelete);
      if (result.success) {
        toast("Staff Account deleted successfully", { type: "success" });
        fetchData(params);
        return true;
      } else {
        if (result.errors?.status) {
          toast("Staff Account cannot be deleted due to not be an Inactive", { type: "error" });
        } else if (result.errors?.members_count) {
          toast("Staff Account cannot be deleted due to having members assigned to him", {
            type: "error",
          });
        }
        return false;
      }
    } catch (e) {
      toast("Staff Account delete failed", { type: "error" });
      return false;
    }
  };

  const onCloseConfirmDelete = async (confirm = false) => {
    if (confirm) {
      handleDeleteUser();
    }
    setShowStaffDeleteModal(false);
  };

  return (
    <div className={"staff-accounts-container"}>
      <Filter
        params={params}
        defaultParams={defaultParams}
        onChange={onChangeFilterHandler}
        onClickCreate={() => {
          setSelectedStaff(null);
          setShowStaffAddEditModal(true);
        }}
      />
      <TableContainer sx={{ paddingBottom: "20px" }} className="staff-accounts-table">
        <Table stickyHeader aria-label="sticky table">
          <TableHeader
            sortingCallback={handleSortChange}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
          />
          <TableBody>
            {!loading &&
              data?.map((item) => (
                <TableItem
                  key={`staff_${item.id}`}
                  data={item}
                  onEdit={(staff) => {
                    setSelectedStaff(staff);
                    setShowStaffAddEditModal(true);
                  }}
                  onUpdateStatus={onUpdateStatus}
                  onDelete={
                    +item.status === 99 && +item.assigned_members === 0
                      ? () => onDeleteConfirm(item.id)
                      : null
                  }
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box display="flex" justifyContent="center">
          <CircularProgress style={{ alignSelf: "center" }} />
        </Box>
      )}

      <Pagination
        {...pagination}
        onPageChange={handlePageChange}
        pageDropdownProps={{
          defaultValue: pagination.per_page,
          onChange: handlePaginationOptionChange,
          value: pagination.per_page,
        }}
      />
      {showStaffAddEditModal && (
        <StaffsAddEditComp
          open={showStaffAddEditModal}
          data={selectedStaff}
          onClose={() => {
            setShowStaffAddEditModal(false);
            setSelectedStaff(null);
          }}
          callback={() => fetchData(params)}
        />
      )}
      <ConfirmationBox
        open={showStaffDeleteModal}
        onClose={onCloseConfirmDelete}
        title={"Are you sure you wish to delete this Staff member?"}
        message={
          <>
            If answered Yes, the Staff Member will be deleted, this action is not possible to be
            reversed.
          </>
        }
        type={"confirmation"}
      />
    </div>
  );
}

export default StaffAccounts;
